import { I18NEXT_NAMESPACE_RESOLVER } from 'angular-i18next';

import {
  CommonRoutes,
  ExtendedRoutes,
  GlobalCareNoteRoutes,
  GlobalCartRoutes,
  GlobalCheckoutRoutes,
  GlobalCxmRoutes,
  GlobalDownloadsRoutes,
  GlobalEContractingRoutes,
  GlobalEditorialRoutes,
  GlobalEvouchersRoutes,
  GlobalFirestopRoutes,
  GlobalMyAccountAuthmanagementRoutes,
  GlobalMyAccountFavoritesRoutes,
  GlobalMyAccountFinHubRoutes,
  GlobalMyAccountProfileRoutes,
  GlobalMyAccountToolsRoutes,
  GlobalMyaccountSubscriptionsRoutes,
  GlobalProductBarcodeScannerRoutes,
  GlobalProductsRoutes,
  GlobalRegistrationRoutes,
  GlobalSearchRoutes,
  GlobalSoftwareFaqRoutes,
  GlobalSubscriptionRoutes,
  GlobalVideosRoutes
} from '@hdms/shared/common-types';
import { canActivateEvoucherLogin, canMatchLoggedIn } from '@hdms/shared/data-access';
import { canMatchEmptyContent, canMatchWithToggle } from '@hdms/shared/util-core';

import { EmptyComponent } from '../components/empty.component';

import { htmlExtensionRouteMatcher } from './extension-route.matcher';
import { regexBaseRouteMatcher } from './regex-base-route.matcher';

const SHARED_COMPARE_NS = 'shared-compare.feature';
const SHARED_STORE_LOCATOR_NS = 'shared-store-locator.feature';
const SEARCH_NS = 'search.feature';

export const APP_ROUTES: ExtendedRoutes = [
  {
    path: GlobalProductBarcodeScannerRoutes.BASE,
    loadChildren: () => import('@hdms/product-barcode-scanner/feature').then(m => m.ProductBarcodeScannerFeatureModule),
    data: {
      i18nextNamespaces: ['product-barcode-scanner.feature'],
      isCompressedPageLayout: true,
      metaData: {
        robotsNoIndex: true
      }
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalCartRoutes.BASE,
    loadChildren: () => import('@hdms/cart/feature').then(m => m.CartFeatureModule),
    data: {
      i18nextNamespaces: ['cart.feature', SHARED_STORE_LOCATOR_NS]
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalCheckoutRoutes.BASE,
    loadChildren: () => import('@hdms/checkout/feature').then(m => m.CheckoutFeatureModule),
    data: {
      i18nextNamespaces: ['checkout.feature', SHARED_STORE_LOCATOR_NS]
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalCxmRoutes.BASE,
    loadChildren: () =>
      import('@hdms/customer-experience-measurement/feature').then(m => m.CustomerExperienceMeasurementFeatureModule),
    data: {
      i18nextNamespaces: ['customer-experience-measurement.feature'],
      metaData: {
        robotsNoIndex: true
      }
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalEvouchersRoutes.BASE,
    canMatch: [canMatchLoggedIn],
    loadChildren: () => import('@hdms/evouchers/feature').then(m => m.EvouchersFeatureModule),
    data: {
      i18nextNamespaces: ['evouchers.feature'],
      preventLoginRedirect: true,
      metaData: {
        robotsNoIndex: true
      }
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalEvouchersRoutes.EVOUCHER_LOGIN,
    canActivate: [canActivateEvoucherLogin],
    component: EmptyComponent,
    data: {
      ssoRedirectRoute: `/${GlobalEvouchersRoutes.BASE}`,
      metaData: {
        robotsNoIndex: true
      }
    }
  },
  {
    path: GlobalMyAccountAuthmanagementRoutes.BASE,
    // TODO: AuthMgmt - Removed canMatch: [canMatchLoggedIn]
    // /libs/myaccount-authmanagement/feature/README.md
    canMatch: [() => false],
    loadChildren: () =>
      import('@hdms/myaccount-authmanagement/feature').then(m => m.MyaccountAuthmanagementFeatureModule),
    data: {
      i18nextNamespaces: ['myaccount-authmanagement.feature'],
      metaData: {
        robotsNoIndex: true
      }
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalMyAccountProfileRoutes.BASE,
    canMatch: [canMatchLoggedIn],
    loadChildren: () => import('@hdms/myaccount-profile/feature').then(m => m.MyaccountProfileFeatureModule),
    data: {
      i18nextNamespaces: ['myaccount-profile.feature'],
      metaData: {
        robotsNoIndex: true
      }
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalMyAccountFinHubRoutes.BASE,
    canMatch: [canMatchLoggedIn],
    loadChildren: () => import('@hdms/financial-hub/feature').then(m => m.FinancialHubFeatureModule),
    data: {
      i18nextNamespaces: ['shared.feature-quote', 'financial-hub.feature'],
      metaData: {
        robotsNoIndex: true
      }
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalMyAccountFavoritesRoutes.BASE,
    canMatch: [canMatchLoggedIn],
    loadChildren: () => import('@hdms/myaccount-favorites/feature').then(m => m.MyaccountFavoritesFeatureModule),
    data: {
      i18nextNamespaces: ['myaccount-favorites.feature'],
      metaData: {
        robotsNoIndex: true
      }
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalMyaccountSubscriptionsRoutes.BASE,
    canMatch: [canMatchLoggedIn],
    loadChildren: () =>
      import('@hdms/myaccount-subscriptions/feature').then(m => m.MyaccountSubscriptionsFeatureModule),
    data: {
      metaData: {
        robotsNoIndex: true
      }
    }
  },
  {
    path: GlobalMyAccountToolsRoutes.BASE,
    canMatch: [canMatchLoggedIn],
    loadChildren: () => import('@hdms/myaccount-tools/feature').then(m => m.MyaccountToolsFeatureModule),
    data: {
      i18nextNamespaces: ['myaccount-tools.feature'],
      metaData: {
        robotsNoIndex: true
      }
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalProductsRoutes.BASE,
    loadChildren: () => import('@hdms/product/feature').then(m => m.ProductFeatureModule),
    data: {
      i18nextNamespaces: [SHARED_COMPARE_NS, SHARED_STORE_LOCATOR_NS, SEARCH_NS]
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    matcher: regexBaseRouteMatcher,
    loadChildren: () => import('@hdms/product/feature').then(m => m.ProductFeatureModule),
    data: {
      i18nextNamespaces: [SHARED_COMPARE_NS],
      path: GlobalProductsRoutes.BASE_REGEX
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalProductsRoutes.BASE_CATEGORY_PAGE,
    loadChildren: () => import('@hdms/product/feature-category-page').then(m => m.ProductFeatureCategoryPageModule),
    data: {
      i18nextNamespaces: ['product.feature-category-page']
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalSubscriptionRoutes.BASE,
    loadChildren: () => import('@hdms/subscription/feature').then(m => m.SubscriptionFeatureModule),
    data: {
      i18nextNamespaces: ['subscription.feature']
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalRegistrationRoutes.BASE,
    canMatch: [canMatchEmptyContent],
    loadChildren: () => import('@hdms/registration/feature').then(m => m.RegistrationFeatureModule),
    data: {
      i18nextNamespaces: ['registration.feature']
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalCareNoteRoutes.BASE,
    canMatch: [canMatchLoggedIn],
    loadChildren: () => import('@hdms/care-notes/feature').then(m => m.CareNotesFeatureModule),
    data: {
      i18nextNamespaces: ['care-notes.feature', 'myaccount-profile.feature'],
      metaData: {
        robotsNoIndex: true
      }
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalSearchRoutes.BASE,
    loadChildren: () => import('@hdms/search/feature').then(m => m.SearchLoaderModule),
    data: {
      i18nextNamespaces: [SEARCH_NS, SHARED_COMPARE_NS]
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalSearchRoutes.SPECIAL_PRODUCTS,
    loadChildren: () => import('@hdms/search/feature').then(m => m.SearchLoaderModule),
    data: {
      i18nextNamespaces: [SEARCH_NS, SHARED_COMPARE_NS]
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalEContractingRoutes.BASE,
    canMatch: [canMatchLoggedIn],
    loadChildren: () => import('@hdms/e-contracting/feature').then(m => m.EContractingFeatureModule),
    data: {
      i18nextNamespaces: ['shared.feature-quote', 'e-contracting.feature-quotation', 'shared-global-labelling.feature'],
      isCompressedPageLayout: true,
      metaData: {
        robotsNoIndex: true
      }
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalMyAccountToolsRoutes.REPAIR_TRACKING,
    canMatch: [canMatchEmptyContent, canMatchWithToggle],
    loadChildren: () =>
      import('@hdms/myaccount-tools/feature-repair-tracking').then(
        m => m.MyaccountToolsFeatureAnonymousRepairTrackingModule
      ),
    data: {
      i18nextNamespaces: ['myaccount-tools.feature-repair-tracking'],
      toggleName: 'ldt_tpm_hdms_anonymous_repair_tracking',
      metaData: {
        robotsNoIndex: true
      }
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    matcher: htmlExtensionRouteMatcher,
    canMatch: [canMatchWithToggle],
    loadChildren: () =>
      import('@hdms/download-center/feature-downloads').then(m => m.DownloadCenterFeatureDownloadsModule),
    data: {
      i18nextNamespaces: [SEARCH_NS, 'download-center.feature-downloads'],
      toggleName: 'ldt_search_decoupling',
      path: GlobalDownloadsRoutes.BASE
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalEditorialRoutes.EDITORIALS,
    canMatch: [canMatchWithToggle],
    loadChildren: () =>
      import('@hdms/download-center/feature-editorial').then(m => m.DownloadCenterFeatureEditorialModule),
    data: {
      i18nextNamespaces: [SEARCH_NS, 'download-center.feature-editorial'],
      toggleName: 'ldt_search_decoupling'
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalFirestopRoutes.BASE,
    canMatch: [canMatchWithToggle],
    loadChildren: () =>
      import('@hdms/download-center/feature-firestop').then(m => m.DownloadCenterFeatureFirestopModule),
    data: {
      i18nextNamespaces: [SEARCH_NS, 'download-center.feature-firestop'],
      toggleName: 'ldt_search_decoupling'
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalSoftwareFaqRoutes.BASE,
    canMatch: [canMatchWithToggle],
    loadChildren: () =>
      import('@hdms/download-center/feature-software-faq').then(m => m.DownloadCenterFeatureSoftwareFaqModule),
    data: {
      i18nextNamespaces: [SEARCH_NS, 'download-center.feature-software-faq'],
      toggleName: 'ldt_search_decoupling'
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: GlobalVideosRoutes.BASE,
    canMatch: [canMatchWithToggle],
    loadChildren: () => import('@hdms/download-center/feature-videos').then(m => m.DownloadCenterFeatureVideosModule),
    data: {
      i18nextNamespaces: [SEARCH_NS, 'download-center.feature-videos'],
      toggleName: 'ldt_search_decoupling'
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  },
  {
    path: CommonRoutes.EMPTY,
    component: EmptyComponent,
    data: {
      cacheConfig: {
        ttl: 86400 // 1 day
      }
    }
  },
  {
    path: '',
    loadChildren: () => import('@hdms/website/feature').then(m => m.WebsiteFeatureModule),
    data: {
      i18nextNamespaces: ['website.feature']
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    }
  }
];
